import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
  type CanActivateFn,
} from '@angular/router';
import { Store } from '@ngxs/store';
import { firstValueFrom } from 'rxjs';

import { LocalStorageService } from '@cosmos/data-access-storage';
import {
  CustomerPortalAuthActions,
  CustomerPortalAuthQueries,
} from '@customer-portal/auth/data-access-auth';
import { hasRetainAccessQueryParam } from '@customer-portal/common/util-retain-access-code';

import {
  getParams,
  getUrlTreeWithoutTokensOrPass,
  handleLoginErrorRedirect,
  tryToRestorePresentationSessionFromStorage,
} from './access.guard.util';

/**
 * This guard is used to handle the authentication on customer portal.
 */
export const customerPortalAuthGuard: CanActivateFn = async function (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Promise<UrlTree | boolean> {
  const store = inject(Store);
  const router = inject(Router);
  const _lsService = inject(LocalStorageService);
  const params = getParams(route);

  const session = store.selectSnapshot(CustomerPortalAuthQueries.getSession);
  // If `session` exists then the user has been already authenticated and we should just
  // allow bypassing this guard.
  // The `!params.token && !params.accessCode` condition is used to ensure the page is not previewed by distributor.
  // Suppose the distributor was logged in with token A, but this token has been expired, and
  // now the distributor has token B. The `getSession` will still return token A, because it was
  // persisted into the session storage.
  if (session && !params.token && !params.accessCode) {
    return true;
  }

  // login by explicit token
  if (params.token) {
    await firstValueFrom(
      store.dispatch(
        new CustomerPortalAuthActions.LoginWithDistributorToken({
          distributorToken: params.token,
        })
      ),
      { defaultValue: undefined }
    );
    // strip the token from url
    return getUrlTreeWithoutTokensOrPass(router, state.url);
  }

  // if there is no explicit accessCode try to restore presentation session from storage
  if (!session && !params.accessCode) {
    if (
      await tryToRestorePresentationSessionFromStorage(store, _lsService, route)
    ) {
      // if session is restored we can redirect to the actual presentation url
      return true;
    }
  }

  // try to login even without accessCode to check if given entity id exists
  if (params.projectId || params.presentationId || params.documentId) {
    try {
      await firstValueFrom(
        store.dispatch(
          new CustomerPortalAuthActions.LoginWithAccessCode({
            // api throws 400 if no access code is provided but we want to get 401 in this case that's why we pass a dummy access code
            accessCode: params.accessCode || 'no-access-code',
            // TODO(dt): This is a temporary workaround.
            // Currently login throws 403 if we login with projectId and accessCode that we get via /api/venus/presentations/presentation_id/sharelink
            // so as a workaround we don't pass projectId in this case.
            projectId:
              params.presentationId &&
              hasRetainAccessQueryParam(route.queryParams)
                ? undefined
                : params.projectId,
            presentationId: params.presentationId,
            orderId: params.documentId,
          })
        ),
        { defaultValue: undefined }
      );
      // - Return new UrlTree with the tokens stripped from the URL if any.
      //   Keep in mind that returning a UrlTree will cause the guard to run again.
      // - If there are no tokens in the URL, simply return "true" so we let navigation
      //   proceed to a child and so we don't get into a loop running this guard again.
      return getUrlTreeWithoutTokensOrPass(router, state.url);
    } catch (error) {
      return handleLoginErrorRedirect(error, router);
    }
  }

  // this branch should never happen, just for semantic purposes
  return router.navigate(['/unauthorized'], { skipLocationChange: true });
};
